import type { ReactNode } from "react";

const SCORE_LABEL_PREFIX = 8;
const SCORE_LABEL_CONTAINS = 4;
const SCORE_CONTEXT_CONTAINS = 2;
const SCORE_DETAILS_CONTAINS = 1;
const SCORE_EXACT_LABEL = 100;
const SCORE_LABEL_STARTS_WITH_NEEDLE = 60;
const SCORE_LABEL_CONTAINS_NEEDLE = 30;

export type SearchPage = {
  path: string;
  label: string;
  context?: string;
  /** Translated card description; searchable without changing the visible breadcrumb. */
  description?: string;
  /** Translated labels of settings/options on this destination. Never include secrets or record data. */
  keywords?: readonly string[];
};
export type SearchEntry = {
  key: string;
  label: string;
  description?: string;
  icon?: ReactNode;
  open: () => void;
  pin?: { active: () => boolean; toggle: () => void };
};
export type SearchGroup = { key: string; label: string; entries: SearchEntry[] };
export type SearchSource = { key: string; load: (term: string, signal: AbortSignal) => Promise<SearchGroup[]> };

/** Only destinations already filtered by the product's actual navigation/settings rights belong here. */
export function matchSearchPages(pages: readonly SearchPage[], term: string, limit = Number.POSITIVE_INFINITY): SearchPage[] {
  const normalize = (text: string) => text.toLowerCase().normalize("NFC")
    .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss")
    .normalize("NFKD").replace(/\p{M}/gu, "").replace(/[^\p{L}\p{N}]+/gu, " ").trim();
  const needle = normalize(term);
  if (!needle) return [];
  const words = [...new Set(needle.split(" "))];
  const score = (page: SearchPage) => {
    const label = normalize(page.label);
    const context = normalize(page.context ?? "");
    const details = normalize([page.description, ...(page.keywords ?? [])].filter(Boolean).join(" "));
    if (!words.every(word => `${label} ${context} ${details}`.includes(word))) return 0;
    const wordScore = words.reduce((sum, word) => sum + (label.split(" ").some(part => part.startsWith(word)) ? SCORE_LABEL_PREFIX : label.includes(word) ? SCORE_LABEL_CONTAINS : context.includes(word) ? SCORE_CONTEXT_CONTAINS : SCORE_DETAILS_CONTAINS), 0);
    return wordScore + (label === needle ? SCORE_EXACT_LABEL : label.startsWith(needle) ? SCORE_LABEL_STARTS_WITH_NEEDLE : label.includes(needle) ? SCORE_LABEL_CONTAINS_NEEDLE : 0);
  };
  const seen = new Set<string>();
  return pages.map(page => ({ page, score: score(page) })).filter(item => item.score > 0)
    .sort((a, b) => b.score - a.score || a.page.label.localeCompare(b.page.label))
    .filter(({ page }) => { if (seen.has(page.path)) return false; seen.add(page.path); return true; })
    .slice(0, limit).map(item => item.page);
}
